/**
 * azure-functions/shared/workflows/validateDefinition.cjs
 *
 * The workflow definition validator and the expected-assurance derivation
 * (docs/workflow-engine-design.md §3a, §3c, §3d, §3e, §3g, §3i). One copy,
 * shared by the browser (the designer's live validation, via
 * src/utils/workflowDefinition.js) and the server (the publish path, which runs
 * it again whatever the client sent — WF-B-001, WF-B-006).
 *
 * validateDefinition never throws on bad input: every problem is a structured
 * error { code, stepId?, path, message }. A definition with any error cannot be
 * published.
 *
 * expectedAssurance never says "compliant": a definition describes the least
 * evidence a passing run can rest on, not whether any control is met
 * (WF-B-005, TRYGGR_INTENT.md §6.2).
 */

'use strict';

const {
  STRENGTHS,
  ACTION_BY_ID,
  END_MARKER,
  JOIN_TYPES,
  SCHEDULE_FREQUENCIES,
  SUBJECT_DATE_REGISTRY,
  strengthRank,
  isNegativeOutcome,
} = require('./actionCatalogue.cjs');
const { FETCH_SOURCE_IDS, isFetchSource, isFetchField } = require('./fetchSources.cjs');

/** Path enumeration ceiling — beyond this the definition is refused, never half-checked. */
const MAX_PATHS = 5000;

/**
 * What a definition that validates has been checked for, in plain words — the
 * designer shows this list when validateDefinition returns valid (PDWB-01), so
 * the screen never keeps its own copy of the rules. Edit it with the rules.
 * Link resolution is NOT on it: that runs only at publish, against the tenant.
 */
const RULES_CHECKED = Object.freeze([
  'The workflow has a title, an owner role and a trigger',
  'The workflow is linked to at least one control, obligation, document or risk',
  'Every step uses an action from the closed catalogue, configured with only its own fields',
  'Every human step has an owner role and a due date',
  'Every path ends in an End, every step is reachable, and nothing loops back',
  'Every parallel group joins over at least one path that is not a dead end, and has a failure route',
  'A failed join never leads to a pass',
  'No pass follows a rejection, an adverse review, a failed sample or a not-effective verdict without a fresh judgement of the same kind',
  'Every passing path rests on Medium-or-higher evidence — never on Notify, Wait or Attest alone',
]);

const MEDIUM = strengthRank('Medium');

/** Human judgements a negative outcome of which only a same-type re-judgement may overturn (§3c rule 9). */
const JUDGEMENT_ACTIONS = new Set(['approve-reject', 'review', 'sample', 'effectiveness-check']);

/** Judgements that name their inputs in `config.reviews` (§3b.6). */
const REVIEWS_ACTIONS = new Set(['approve-reject', 'review']);

/** Actions that introduce new evidence for a later judgement to rest on. */
const GATHER_ACTIONS = new Set(['collect-data', 'fetch-data', 'upload-artifact', 'attest']);

/** Actions whose recorded data a Branch may route on by `field`. */
const DATA_ACTIONS = new Set(['collect-data', 'fetch-data']);

const DEFINITION_KEYS = new Set([
  'definitionId',
  'version',
  'status',
  'title',
  'description',
  'ownerRole',
  'trigger',
  'links',
  'steps',
  // Server-derived / server-stamped: accepted in the shape, never trusted
  // (the store restores them — WF-B-005), never validated as authored input.
  'expectedStrength',
  'publishedAt',
  'publishedBy',
  'createdAt',
  'createdBy',
]);

const STEP_KEYS = new Set([
  'id',
  'action',
  'title',
  'description',
  'ownerRole',
  'dueAfterDays',
  'config',
  'next',
  'onFail',
]);

const LINK_ARRAY_KEYS = ['controls', 'obligations', 'documents', 'risks'];

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasText = (v) => typeof v === 'string' && v.trim().length > 0;
const isPosInt = (v) => Number.isInteger(v) && v >= 1;

function specFor(action) {
  if (action === END_MARKER.id) return END_MARKER;
  return ACTION_BY_ID[action] || null;
}

function makeCollector() {
  const errors = [];
  const seen = new Set();
  const add = (code, path, message, stepId) => {
    const key = `${code}|${stepId || ''}|${path}`;
    if (seen.has(key)) return;
    seen.add(key);
    const e = { code, path, message };
    if (stepId) e.stepId = stepId;
    errors.push(e);
  };
  return { errors, add };
}

// ── Top level, trigger and links ────────────────────────────────────────────

function checkTrigger(def, add) {
  const t = def.trigger;
  if (!isObj(t)) {
    add(
      'trigger-required',
      'trigger',
      'A definition needs a trigger (manual, schedule or subject-date).'
    );
    return;
  }
  const obligations =
    isObj(def.links) && Array.isArray(def.links.obligations) ? def.links.obligations : [];
  const allowed = {
    manual: ['type'],
    schedule: ['type', 'frequency', 'cadence'],
    'subject-date': ['type', 'subjectType', 'dateField'],
  };
  if (!allowed[t.type]) {
    add('trigger-type-unknown', 'trigger.type', `Unknown trigger type "${t.type}".`);
    return;
  }
  Object.keys(t).forEach((k) => {
    if (!allowed[t.type].includes(k))
      add(
        'trigger-field-not-allowed',
        `trigger.${k}`,
        `"${k}" is not allowed on a ${t.type} trigger.`
      );
  });
  if (t.type === 'schedule') {
    const hasFreq = t.frequency !== undefined;
    const hasCadence = t.cadence !== undefined;
    if (hasFreq === hasCadence) {
      add(
        'schedule-needs-one-cadence',
        'trigger',
        'A schedule trigger takes exactly one of a fixed frequency or the linked obligation cadence.'
      );
    }
    if (hasFreq && !SCHEDULE_FREQUENCIES.includes(t.frequency)) {
      add(
        'schedule-frequency-unknown',
        'trigger.frequency',
        `Frequency must be one of ${SCHEDULE_FREQUENCIES.join(', ')}.`
      );
    }
    if (hasFreq && obligations.length > 0) {
      add(
        'fixed-frequency-with-obligation',
        'trigger.frequency',
        "A definition that links an obligation runs on the obligation's cadence; a second, fixed frequency could disagree with it."
      );
    }
    if (hasCadence && t.cadence !== 'obligation') {
      add(
        'schedule-cadence-unknown',
        'trigger.cadence',
        'The only cadence a schedule may borrow is "obligation".'
      );
    }
    if (hasCadence && t.cadence === 'obligation' && obligations.length === 0) {
      add(
        'obligation-cadence-without-obligation',
        'trigger.cadence',
        'An obligation cadence needs at least one linked obligation.'
      );
    }
  }
  if (t.type === 'subject-date') {
    const entry = SUBJECT_DATE_REGISTRY[t.subjectType];
    if (!entry) {
      add(
        'subject-date-unavailable',
        'trigger.subjectType',
        `"${t.subjectType}" is not a registered subject type for date triggers.`
      );
    } else if (!Array.isArray(entry.dateFields) || !entry.dateFields.includes(t.dateField)) {
      add(
        'subject-date-field-unknown',
        'trigger.dateField',
        `"${t.dateField}" is not a registered date field for ${t.subjectType}.`
      );
    }
  }
}

function toLookup(v) {
  if (v instanceof Set) return (id) => v.has(id);
  if (Array.isArray(v)) return (id) => v.includes(id);
  if (typeof v === 'function') return v;
  return null;
}

function checkLinks(def, add, options) {
  const links = def.links;
  if (links === undefined) return;
  if (!isObj(links)) {
    add('links-invalid', 'links', 'Links must be an object.');
    return;
  }
  Object.keys(links).forEach((k) => {
    if (!LINK_ARRAY_KEYS.includes(k) && k !== 'criticality')
      add('links-field-not-allowed', `links.${k}`, `"${k}" is not a link type.`);
  });
  LINK_ARRAY_KEYS.forEach((k) => {
    if (links[k] === undefined) return;
    if (!Array.isArray(links[k]) || !links[k].every(hasText)) {
      add('links-invalid', `links.${k}`, `links.${k} must be a list of ids.`);
    } else if (new Set(links[k]).size !== links[k].length) {
      add('links-duplicate', `links.${k}`, `links.${k} lists an id twice.`);
    }
  });
  if (links.criticality !== undefined) {
    const c = links.criticality;
    if (!isObj(c) || !hasText(c.minLevelId) || Object.keys(c).some((k) => k !== 'minLevelId')) {
      add(
        'links-invalid',
        'links.criticality',
        'links.criticality must be { minLevelId } naming a level of the tenant criticality scale.'
      );
    }
  }

  // Every link id must resolve for the tenant at publish (design §3e). The
  // publish path passes the tenant's ids; a publish without them is refused,
  // never waved through.
  if (!options.forPublish) return;
  const known = isObj(options.known) ? options.known : {};
  const checks = [
    ...LINK_ARRAY_KEYS.map((k) => [k, Array.isArray(links[k]) ? links[k] : [], known[k]]),
    [
      'criticalityLevels',
      isObj(links.criticality) && hasText(links.criticality.minLevelId)
        ? [links.criticality.minLevelId]
        : [],
      known.criticalityLevels,
    ],
  ];
  checks.forEach(([k, ids, source]) => {
    if (ids.length === 0) return;
    const has = toLookup(source);
    if (!has) {
      add(
        'link-resolution-unavailable',
        `links.${k}`,
        `Cannot publish: the tenant's ${k} were not supplied to resolve the links against.`
      );
      return;
    }
    ids.forEach((id) => {
      if (!has(id)) add('link-unresolved', `links.${k}`, `"${id}" does not exist in this tenant.`);
    });
  });
}

// ── Per-step shape ──────────────────────────────────────────────────────────

function checkConfigField(name, spec, value, path, add, stepId) {
  const bad = (msg) => add('config-field-invalid', `${path}.${name}`, msg, stepId);
  switch (spec.type) {
    case 'string':
      if (!hasText(value)) bad(`${name} must be non-empty text.`);
      break;
    case 'integer':
      if (!isPosInt(value)) bad(`${name} must be a whole number of at least 1.`);
      break;
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) bad(`${name} must be a number.`);
      break;
    case 'enum':
      if (!spec.values.includes(value)) bad(`${name} must be one of ${spec.values.join(', ')}.`);
      break;
    case 'stepId':
      if (!hasText(value)) bad(`${name} must name a step.`);
      break;
    case 'stepIds':
      if (!Array.isArray(value) || value.length === 0 || !value.every(hasText))
        bad(`${name} must name at least one step.`);
      break;
    case 'fields': {
      if (!Array.isArray(value) || value.length === 0) {
        bad('fields must list at least one field to collect.');
        break;
      }
      const keys = new Set();
      value.forEach((f, i) => {
        if (!isObj(f) || !hasText(f.key) || !hasText(f.label)) {
          add(
            'config-field-invalid',
            `${path}.fields[${i}]`,
            'Each field needs a key and a label.',
            stepId
          );
        } else if (keys.has(f.key)) {
          add(
            'config-field-invalid',
            `${path}.fields[${i}]`,
            `Field key "${f.key}" is used twice.`,
            stepId
          );
        } else {
          keys.add(f.key);
        }
      });
      break;
    }
    case 'routes': {
      if (!Array.isArray(value) || value.length === 0) {
        bad('A branch needs at least one route.');
        break;
      }
      const whens = new Set();
      value.forEach((r, i) => {
        if (
          !isObj(r) ||
          !hasText(r.when) ||
          !hasText(r.next) ||
          Object.keys(r).some((k) => k !== 'when' && k !== 'next')
        ) {
          add(
            'config-field-invalid',
            `${path}.routes[${i}]`,
            'Each route is { when, next } and nothing else.',
            stepId
          );
        } else if (whens.has(r.when)) {
          add(
            'config-field-invalid',
            `${path}.routes[${i}]`,
            `Route "${r.when}" is declared twice.`,
            stepId
          );
        } else {
          whens.add(r.when);
        }
      });
      break;
    }
    case 'children': {
      if (!Array.isArray(value) || value.length < 2) {
        add(
          'group-too-few-children',
          `${path}.children`,
          'A parallel group needs at least two children.',
          stepId
        );
        if (!Array.isArray(value)) break;
      }
      const starts = new Set();
      value.forEach((c, i) => {
        const okKeys = isObj(c) && Object.keys(c).every((k) => k === 'start' || k === 'deadEnd');
        if (
          !okKeys ||
          !hasText(c.start) ||
          (c.deadEnd !== undefined && typeof c.deadEnd !== 'boolean')
        ) {
          add(
            'config-field-invalid',
            `${path}.children[${i}]`,
            'Each child is { start, deadEnd? } and nothing else.',
            stepId
          );
        } else if (starts.has(c.start)) {
          add(
            'config-field-invalid',
            `${path}.children[${i}]`,
            `Child "${c.start}" is listed twice.`,
            stepId
          );
        } else {
          starts.add(c.start);
        }
      });
      break;
    }
    case 'join':
      if (
        !isObj(value) ||
        !JOIN_TYPES.includes(value.type) ||
        Object.keys(value).some((k) => k !== 'type' && k !== 'n')
      ) {
        add(
          'group-join-invalid',
          `${path}.join`,
          `The join rule must be one of ${JOIN_TYPES.join(', ')}.`,
          stepId
        );
      } else if (value.type !== 'n-of-m' && value.n !== undefined) {
        add('group-join-invalid', `${path}.join.n`, 'Only an n-of-m join takes n.', stepId);
      }
      break;
    default:
      bad(`${name} has an unknown field type.`);
  }
}

function checkStepShape(step, i, ctx) {
  const { add, ids } = ctx;
  const path = `steps[${i}]`;
  if (!isObj(step)) {
    add('step-invalid', path, 'A step must be an object.');
    return;
  }
  const stepId = hasText(step.id) ? step.id : undefined;
  if (!stepId) add('step-id-required', `${path}.id`, 'Every step needs an id.');

  Object.keys(step).forEach((k) => {
    if (!STEP_KEYS.has(k))
      add('step-field-not-allowed', `${path}.${k}`, `"${k}" is not a step field.`, stepId);
  });

  const spec = specFor(step.action);
  if (!spec) {
    add(
      'action-unknown',
      `${path}.action`,
      `"${step.action}" is not in the action catalogue. Actions are configured, never invented.`,
      stepId
    );
    return;
  }

  if (step.title !== undefined && !hasText(step.title))
    add('step-field-invalid', `${path}.title`, 'title must be non-empty text.', stepId);
  if (step.description !== undefined && typeof step.description !== 'string') {
    add('step-field-invalid', `${path}.description`, 'description must be text.', stepId);
  }

  // Owners and due dates belong to human steps only.
  if (spec.humanStep) {
    if (!hasText(step.ownerRole))
      add(
        'owner-role-required',
        `${path}.ownerRole`,
        'Every human step needs an owner role.',
        stepId
      );
    // Every human step owes a due date, so an open task always turns overdue
    // somewhere visible instead of waiting silently (PDWA-04 co-owner, §12).
    if (step.dueAfterDays === undefined) {
      add(
        'human-due-required',
        `${path}.dueAfterDays`,
        'Every human step needs a due date (dueAfterDays) so its task can become overdue.',
        stepId
      );
    } else if (!isPosInt(step.dueAfterDays)) {
      add(
        'step-field-invalid',
        `${path}.dueAfterDays`,
        'dueAfterDays must be a whole number of at least 1.',
        stepId
      );
    }
  } else {
    if (step.ownerRole !== undefined)
      add(
        'owner-role-not-allowed',
        `${path}.ownerRole`,
        'Only a human step has an owner role.',
        stepId
      );
    if (step.dueAfterDays !== undefined)
      add('due-not-allowed', `${path}.dueAfterDays`, 'Only a human step has a due date.', stepId);
  }

  // Config: only the catalogue's fields, every required one present.
  const config = step.config === undefined ? {} : step.config;
  if (!isObj(config)) {
    add('config-invalid', `${path}.config`, 'config must be an object.', stepId);
  } else {
    Object.keys(config).forEach((k) => {
      if (!spec.configFields[k])
        add(
          'config-field-not-allowed',
          `${path}.config.${k}`,
          `"${k}" is not configurable on ${spec.label}.`,
          stepId
        );
    });
    Object.entries(spec.configFields).forEach(([name, fieldSpec]) => {
      if (config[name] === undefined) {
        if (fieldSpec.required)
          add(
            'config-field-required',
            `${path}.config.${name}`,
            `${spec.label} needs ${name}.`,
            stepId
          );
        return;
      }
      checkConfigField(name, fieldSpec, config[name], `${path}.config`, add, stepId);
    });
    // Fetch data reads only a source a working connector writes (PDWA-04,
    // fetchSources.cjs) — a source nothing writes would publish a definition
    // no run could ever honour.
    if (step.action === 'fetch-data' && config.source !== undefined && !isFetchSource(config.source)) {
      add(
        'fetch-source-unsupported',
        `${path}.config.source`,
        `No integration the workflow engine can read writes "${config.source}". Fetch data reads one of: ${FETCH_SOURCE_IDS.join(', ')}. Otherwise use Upload artifact or Collect data.`,
        stepId
      );
    }
    if (step.action === 'sample') {
      const hasPct = config.percent !== undefined;
      const hasCount = config.count !== undefined;
      if (hasPct === hasCount)
        add(
          'sample-size-invalid',
          `${path}.config`,
          'A sample takes exactly one of percent or count.',
          stepId
        );
      if (
        hasPct &&
        (typeof config.percent !== 'number' || config.percent <= 0 || config.percent > 100)
      ) {
        add(
          'sample-size-invalid',
          `${path}.config.percent`,
          'percent must be above 0 and at most 100.',
          stepId
        );
      }
    }
  }

  // Successors.
  const isEnd = step.action === END_MARKER.id;
  const isBranch = step.action === 'branch';
  const isGroup = step.action === 'parallel-group';
  if (isEnd || isBranch) {
    if (step.next !== undefined)
      add('next-not-allowed', `${path}.next`, `${spec.label} does not take next.`, stepId);
  } else if (!hasText(step.next)) {
    add('next-required', `${path}.next`, 'Every path must continue to a step or an End.', stepId);
  }
  if (isGroup) {
    if (!hasText(step.onFail))
      add(
        'group-onfail-required',
        `${path}.onFail`,
        'A parallel group must declare where a failed join goes.',
        stepId
      );
  } else if (step.onFail !== undefined) {
    add('onfail-not-allowed', `${path}.onFail`, 'Only a parallel group takes onFail.', stepId);
  }

  // Every referenced step must exist.
  const refs = [];
  if (hasText(step.next)) refs.push([`${path}.next`, step.next]);
  if (hasText(step.onFail)) refs.push([`${path}.onFail`, step.onFail]);
  if (isObj(config)) {
    if (isBranch) {
      if (hasText(config.on)) refs.push([`${path}.config.on`, config.on]);
      if (hasText(config.otherwise)) refs.push([`${path}.config.otherwise`, config.otherwise]);
      (Array.isArray(config.routes) ? config.routes : []).forEach((r, j) => {
        if (isObj(r) && hasText(r.next)) refs.push([`${path}.config.routes[${j}].next`, r.next]);
      });
    }
    if (isGroup && Array.isArray(config.children)) {
      config.children.forEach((c, j) => {
        if (isObj(c) && hasText(c.start))
          refs.push([`${path}.config.children[${j}].start`, c.start]);
      });
    }
    if (Array.isArray(config.reviews)) {
      config.reviews.forEach((r, j) => {
        if (hasText(r)) refs.push([`${path}.config.reviews[${j}]`, r]);
      });
    }
  }
  refs.forEach(([p, target]) => {
    if (!ids.has(target))
      add('unknown-step-ref', p, `"${target}" is not a step in this definition.`, stepId);
    else if (target === stepId) add('cycle', p, 'A step cannot lead to itself.', stepId);
  });
}

// ── Graph ───────────────────────────────────────────────────────────────────

/** In-scope successors (the parallel group's children start new scopes). */
function scopeSuccessors(step) {
  const out = [];
  const c = isObj(step.config) ? step.config : {};
  if (step.action === 'branch') {
    (c.routes || []).forEach((r) => out.push(r.next));
    if (hasText(c.otherwise)) out.push(c.otherwise);
  } else {
    if (hasText(step.next)) out.push(step.next);
    if (hasText(step.onFail)) out.push(step.onFail);
  }
  return out;
}

function childStarts(step) {
  if (
    step.action !== 'parallel-group' ||
    !isObj(step.config) ||
    !Array.isArray(step.config.children)
  )
    return [];
  return step.config.children;
}

function findCycle(byId, order) {
  const state = new Map(); // 1 = on stack, 2 = done
  const topo = [];
  let cycleAt = null;
  const visit = (id) => {
    if (cycleAt) return;
    const s = state.get(id);
    if (s === 2) return;
    if (s === 1) {
      cycleAt = id;
      return;
    }
    state.set(id, 1);
    const step = byId.get(id);
    [...scopeSuccessors(step), ...childStarts(step).map((c) => c.start)].forEach(visit);
    state.set(id, 2);
    topo.push(id);
  };
  order.forEach(visit);
  return { cycleAt, topo: topo.reverse() };
}

/**
 * Assigns every reachable step to exactly one scope: the main flow, or one
 * parallel child. Edges never cross a scope; a child path ends at its own End.
 */
function assignScopes(byId, entryId, add, indexOf) {
  const scopeOf = new Map();
  const scopes = [{ id: 'main', start: entryId, deadEnd: false, group: null }];
  for (let s = 0; s < scopes.length; s++) {
    const scope = scopes[s];
    const queue = [scope.start];
    while (queue.length) {
      const id = queue.shift();
      const prior = scopeOf.get(id);
      if (prior !== undefined) {
        if (prior !== scope.id) {
          add(
            'cross-scope-edge',
            `steps[${indexOf.get(id)}]`,
            'A step is reached from two different paths (a parallel child and something outside it). Each child path ends at its own End.',
            id
          );
        }
        continue;
      }
      scopeOf.set(id, scope.id);
      const step = byId.get(id);
      queue.push(...scopeSuccessors(step));
      childStarts(step).forEach((c, j) => {
        scopes.push({
          id: `${id}#${j}`,
          start: c.start,
          deadEnd: scope.deadEnd || c.deadEnd === true,
          group: id,
        });
      });
    }
  }
  return { scopeOf, scopes };
}

/** dom(n): the steps on every path from the entry to n (n included). */
function dominators(byId, topo, entryId) {
  const preds = new Map(topo.map((id) => [id, []]));
  topo.forEach((id) => {
    const step = byId.get(id);
    [...scopeSuccessors(step), ...childStarts(step).map((c) => c.start)].forEach((t) => {
      if (preds.has(t)) preds.get(t).push(id);
    });
  });
  const dom = new Map();
  topo.forEach((id) => {
    if (id === entryId) {
      dom.set(id, new Set([id]));
      return;
    }
    const ps = preds.get(id).filter((p) => dom.has(p));
    if (ps.length === 0) return; // unreachable
    let acc = new Set(dom.get(ps[0]));
    ps.slice(1).forEach((p) => {
      const d = dom.get(p);
      acc = new Set([...acc].filter((x) => d.has(x)));
    });
    acc.add(id);
    dom.set(id, acc);
  });
  return dom;
}

// ── Path analysis ───────────────────────────────────────────────────────────

/**
 * Walks every path of every scope from its start to an End. A path is a list
 * of tokens: a step, or a parallel group summarised by its join (§3c).
 * Outcome-bearing steps carry a class on the path — 'pos', 'neg' or
 * 'unresolved' (a plain `next` is taken on every outcome, the negative ones
 * included) — set by a Branch on that step.
 */
function analysePaths(byId, scopes, indexOf, add) {
  let budget = MAX_PATHS;
  let exhausted = false;
  const groupMemo = new Map();
  const scopeByGroupChild = new Map(scopes.map((s) => [s.id, s]));

  const stepToken = (step) => ({ kind: 'step', id: step.id, action: step.action });

  function judgementRule(tokens, classes, endId) {
    tokens.forEach((t, i) => {
      if (t.kind === 'group' && t.failed) {
        add(
          'pass-after-group-fail',
          `steps[${indexOf.get(t.id)}].onFail`,
          'A pass End is reachable after this group failed its join. A failed group routes to a fail End or a finding.',
          t.id
        );
        return;
      }
      if (t.kind !== 'step' || !JUDGEMENT_ACTIONS.has(t.action)) return;
      const cls = classes.get(t.id);
      if (cls === 'pos') return;
      const after = tokens.slice(i + 1);
      const provides = (x) =>
        x.kind === 'step' ? x.action === t.action : !x.failed && x.provides.has(t.action);
      const gathers = (x) =>
        x.kind === 'step' ? GATHER_ACTIONS.has(x.action) : !x.failed && x.gathers;
      let lastJudge = -1;
      let lastGather = -1;
      after.forEach((x, j) => {
        if (provides(x)) lastJudge = j;
        if (gathers(x)) lastGather = j;
      });
      // An Approve / Review re-judgement must name every piece of new evidence
      // gathered since the negative outcome in its `reviews` — otherwise the
      // new evidence reaches the pass unjudged (co-owner, PDWA-02).
      let unjudged = false;
      if (lastJudge !== -1 && REVIEWS_ACTIONS.has(t.action)) {
        const judge = after[lastJudge];
        const named =
          judge.kind === 'step' ? new Set(byId.get(judge.id).config.reviews || []) : null;
        unjudged = after
          .slice(0, lastJudge)
          .some((x) => gathers(x) && (!named || x.kind !== 'step' || !named.has(x.id)));
      }
      if (lastJudge === -1 || lastGather >= lastJudge || unjudged) {
        const spec = ACTION_BY_ID[t.action];
        add(
          'pass-after-negative-outcome',
          `steps[${indexOf.get(t.id)}]`,
          `End "${endId}" (pass) is reachable after a negative ${spec.label} outcome without a fresh ${spec.label} that reviews all the new evidence. A negative judgement is only overturned by a new judgement of the same kind.`,
          t.id
        );
      }
    });
  }

  function awareRank(tokens, classes) {
    let best = -1;
    tokens.forEach((t) => {
      if (t.kind === 'group') {
        if (!t.failed) best = Math.max(best, t.awareRank);
        return;
      }
      const spec = ACTION_BY_ID[t.action];
      if (!spec || spec.strength === null) return;
      if (spec.outcomes.negative.length > 0 && classes.get(t.id) !== 'pos') return;
      best = Math.max(best, strengthRank(spec.strength));
    });
    return best;
  }

  function literalRank(tokens) {
    let best = -1;
    tokens.forEach((t) => {
      if (t.kind === 'group') {
        if (!t.failed) best = Math.max(best, t.literalRank);
        return;
      }
      const spec = ACTION_BY_ID[t.action];
      if (spec && spec.strength !== null) best = Math.max(best, strengthRank(spec.strength));
    });
    return best;
  }

  function actionSet(tokens) {
    const set = new Set();
    tokens.forEach((t) => {
      if (t.kind === 'step') set.add(t.action);
      else if (!t.failed) t.provides.forEach((a) => set.add(a));
    });
    return set;
  }

  function walkScope(startId) {
    const pass = [];
    let fails = 0;
    const walk = (id, tokens, classes) => {
      if (exhausted) return;
      const step = byId.get(id);
      if (step.action === END_MARKER.id) {
        if (--budget < 0) {
          exhausted = true;
          return;
        }
        if (step.config.outcome === 'pass') {
          judgementRule(tokens, classes, id);
          pass.push({
            endId: id,
            aware: awareRank(tokens, classes),
            literal: literalRank(tokens),
            actions: actionSet(tokens),
            gathers: tokens.some((t) =>
              t.kind === 'step' ? GATHER_ACTIONS.has(t.action) : !t.failed && t.gathers
            ),
          });
        } else {
          fails += 1;
        }
        return;
      }
      if (step.action === 'branch') {
        const c = step.config;
        const src = byId.get(c.on);
        const srcSpec = src && ACTION_BY_ID[src.action];
        const local = tokens.some((t) => t.kind === 'step' && t.id === c.on);
        const withClass = (cls) => {
          if (!local || !srcSpec || srcSpec.outcomes.negative.length === 0) return classes;
          const prior = classes.get(c.on);
          if (prior !== 'unresolved' && prior !== cls) return null; // contradicts an earlier branch on this path
          return new Map(classes).set(c.on, cls);
        };
        if (hasText(c.field)) {
          c.routes.forEach((r) => {
            const next = withClass('pos'); // a routed value means the data was obtained
            if (next) walk(r.next, tokens, next);
          });
          if (hasText(c.otherwise)) walk(c.otherwise, tokens, classes);
          return;
        }
        const outs = srcSpec ? [...srcSpec.outcomes.positive, ...srcSpec.outcomes.negative] : [];
        c.routes.forEach((r) => {
          const next = withClass(isNegativeOutcome(src.action, r.when) ? 'neg' : 'pos');
          if (next) walk(r.next, tokens, next);
        });
        if (hasText(c.otherwise)) {
          const routed = new Set(c.routes.map((r) => r.when));
          const left = outs.filter((o) => !routed.has(o));
          if (left.some((o) => isNegativeOutcome(src.action, o))) {
            const next = withClass('neg');
            if (next) walk(c.otherwise, tokens, next);
          }
          if (left.some((o) => !isNegativeOutcome(src.action, o))) {
            const next = withClass('pos');
            if (next) walk(c.otherwise, tokens, next);
          }
        }
        return;
      }
      if (step.action === 'parallel-group') {
        const g = groupSummary(step);
        if (g.canPass)
          walk(step.next, [...tokens, { kind: 'group', id, failed: false, ...g }], classes);
        walk(step.onFail, [...tokens, { kind: 'group', id, failed: true }], classes);
        return;
      }
      const spec = ACTION_BY_ID[step.action];
      const nextClasses =
        spec.outcomes.negative.length > 0 ? new Map(classes).set(id, 'unresolved') : classes;
      walk(step.next, [...tokens, stepToken(step)], nextClasses);
    };
    walk(startId, [], new Map());
    return { pass, fails };
  }

  function groupSummary(step) {
    if (groupMemo.has(step.id)) return groupMemo.get(step.id);
    const children = step.config.children.map((c, j) => ({
      deadEnd: scopeByGroupChild.get(`${step.id}#${j}`).deadEnd,
      ...walkScope(c.start),
    }));
    const joining = children.filter((c) => !c.deadEnd);
    const m = joining.length;
    const join = step.config.join;
    const required = join.type === 'all' ? m : join.type === 'any' ? 1 : join.n;
    const passable = joining.filter((c) => c.pass.length > 0);
    const canPass =
      m > 0 && Number.isInteger(required) && required >= 1 && passable.length >= required;
    let summary;
    if (!canPass) {
      if (m > 0 && Number.isInteger(required) && required >= 1 && required <= m) {
        add(
          'group-cannot-pass',
          `steps[${indexOf.get(step.id)}]`,
          'Too few joining children can ever end pass to satisfy this join.',
          step.id
        );
      }
      summary = { canPass: false };
    } else {
      const minOf = (c, k) => Math.min(...c.pass.map((p) => p[k]));
      const kth = (k) => passable.map((c) => minOf(c, k)).sort((a, b) => a - b)[required - 1];
      const intersect = (sets) =>
        sets.reduce((acc, s) => new Set([...acc].filter((x) => s.has(x))));
      const childCommon = (c) => intersect(c.pass.map((p) => p.actions));
      let provides;
      if (join.type === 'all') {
        provides = new Set();
        passable.forEach((c) => childCommon(c).forEach((a) => provides.add(a)));
      } else {
        provides = intersect(passable.map(childCommon));
      }
      summary = {
        canPass: true,
        awareRank: kth('aware'),
        literalRank: kth('literal'),
        provides,
        gathers: passable.some((c) => c.pass.some((p) => p.gathers)),
      };
    }
    groupMemo.set(step.id, summary);
    return summary;
  }

  // Walk every scope (the main flow and every child, dead ends included) so
  // §3c rule 9 is checked on every pass End, not only the main one.
  const results = new Map();
  scopes.forEach((s) => {
    if (!exhausted) results.set(s.id, walkScope(s.start));
  });
  if (exhausted) {
    add(
      'too-complex',
      'steps',
      `The definition has more than ${MAX_PATHS} distinct paths; it cannot be checked and so cannot be published. Split it.`
    );
  }
  return { main: results.get('main'), exhausted };
}

// ── Entry point ─────────────────────────────────────────────────────────────

/**
 * @param {object} def      a WorkflowDefinition (design §3a, §3i)
 * @param {object} [options]
 * @param {boolean} [options.forPublish]  also resolve every link id for the tenant
 * @param {object}  [options.known]       { controls, obligations, documents, risks,
 *                                          criticalityLevels } — Set, array or predicate
 * @returns {{ valid: boolean, errors: Array<{code, stepId?, path, message}>, analysis: object }}
 */
function validateDefinition(def, options = {}) {
  const { errors, add } = makeCollector();
  const analysis = {
    entryStepId: null,
    deadEndStepIds: [],
    expectedStrength: null,
    minimumPassStrength: null,
  };
  const done = () => ({ valid: errors.length === 0, errors, analysis });
  const opts = isObj(options) ? options : {};

  if (!isObj(def)) {
    add('definition-invalid', '', 'A workflow definition must be an object.');
    return done();
  }

  Object.keys(def).forEach((k) => {
    if (!DEFINITION_KEYS.has(k))
      add('definition-field-not-allowed', k, `"${k}" is not a definition field.`);
  });
  if (!hasText(def.title)) add('title-required', 'title', 'A definition needs a title.');
  if (!hasText(def.ownerRole))
    add(
      'owner-role-required',
      'ownerRole',
      'A definition needs an owner role — who answers for the definition itself.'
    );
  if (def.description !== undefined && typeof def.description !== 'string')
    add('definition-field-invalid', 'description', 'description must be text.');
  checkTrigger(def, add);
  checkLinks(def, add, opts);
  // A workflow exists to operate a requirement (module purpose `job`); one
  // linked to no control, obligation, document or risk produces evidence
  // that reaches nothing (founder decision D7 = A, 2026-10-06).
  if (!LINK_ARRAY_KEYS.some((k) => isObj(def.links) && Array.isArray(def.links[k]) && def.links[k].length > 0)) {
    add(
      'links-required',
      'links',
      'Link the workflow to at least one control, obligation, document or risk — the requirement it operates.'
    );
  }

  if (!Array.isArray(def.steps) || def.steps.length === 0) {
    add('steps-required', 'steps', 'A definition needs at least one step.');
    return done();
  }

  // Unique ids, then shape.
  const ids = new Set();
  const indexOf = new Map();
  def.steps.forEach((s, i) => {
    if (!isObj(s) || !hasText(s.id)) return;
    if (ids.has(s.id))
      add('duplicate-step-id', `steps[${i}].id`, `Step id "${s.id}" is used twice.`, s.id);
    ids.add(s.id);
    if (!indexOf.has(s.id)) indexOf.set(s.id, i);
  });
  const shapeCtx = { add, ids };
  def.steps.forEach((s, i) => checkStepShape(s, i, shapeCtx));

  // Graph analysis needs a well-formed graph; on a malformed one it would only
  // repeat the shape errors already reported.
  if (errors.some((e) => e.path === 'steps' || e.path.startsWith('steps['))) return done();

  const byId = new Map(def.steps.map((s) => [s.id, s]));
  const entryId = def.steps[0].id;
  analysis.entryStepId = entryId;

  // No back-edges (v1 has no loops — design §3h, WF-B-006).
  const { cycleAt, topo } = findCycle(byId, [entryId, ...def.steps.map((s) => s.id)]);
  if (cycleAt) {
    add(
      'cycle',
      `steps[${indexOf.get(cycleAt)}]`,
      'The definition loops back on itself. v1 has no loops: rework is a fail, a finding, or the next run.',
      cycleAt
    );
    return done();
  }

  const { scopeOf, scopes } = assignScopes(byId, entryId, add, indexOf);
  def.steps.forEach((s, i) => {
    if (!scopeOf.has(s.id))
      add(
        'unreachable-step',
        `steps[${i}]`,
        'No path from the first step reaches this step.',
        s.id
      );
  });

  // Dead ends (§3d): excluded from the join, but owned and dated so they end —
  // every human step's due date is required by the shape check above
  // (human-due-required, which since PDWA-04 covers dead ends and the main flow alike).
  const deadScopes = new Set(scopes.filter((s) => s.deadEnd).map((s) => s.id));
  def.steps.forEach((s) => {
    if (deadScopes.has(scopeOf.get(s.id))) analysis.deadEndStepIds.push(s.id);
  });

  // Parallel groups: join arithmetic over joining (non-dead-end) children.
  def.steps.forEach((s, i) => {
    if (s.action !== 'parallel-group') return;
    const m = s.config.children.filter((c) => c.deadEnd !== true).length;
    if (m === 0) {
      add(
        'group-all-dead-ends',
        `steps[${i}].config.children`,
        'Every child of this group is a dead end, so there is nothing to join.',
        s.id
      );
    } else if (s.config.join.type === 'n-of-m') {
      const n = s.config.join.n;
      if (!Number.isInteger(n) || n < 1 || n > m) {
        add(
          'group-join-n-invalid',
          `steps[${i}].config.join.n`,
          `n must be a whole number from 1 to ${m} (the joining children).`,
          s.id
        );
      }
    }
  });

  // Branches and reviews may only reference steps that have run on every path to them.
  const dom = dominators(byId, topo, entryId);
  def.steps.forEach((s, i) => {
    const before = dom.get(s.id);
    if (!before) return;
    if (s.action === 'branch') {
      const c = s.config;
      const src = byId.get(c.on);
      const srcSpec = ACTION_BY_ID[src.action];
      if (
        !srcSpec ||
        src.action === 'branch' ||
        src.action === 'parallel-group' ||
        c.on === s.id ||
        !before.has(c.on)
      ) {
        add(
          'branch-source-invalid',
          `steps[${i}].config.on`,
          'A branch routes on an action that has completed on every path to it.',
          s.id
        );
        return;
      }
      if (hasText(c.field)) {
        if (!DATA_ACTIONS.has(src.action)) {
          add(
            'branch-field-source-invalid',
            `steps[${i}].config.field`,
            'Only Collect data and Fetch data record fields a branch can route on.',
            s.id
          );
        }
        if (
          src.action === 'collect-data' &&
          !(src.config.fields || []).some((f) => f.key === c.field)
        ) {
          add(
            'branch-field-unknown',
            `steps[${i}].config.field`,
            `"${c.field}" is not a field that step collects.`,
            s.id
          );
        }
        if (src.action === 'fetch-data' && !isFetchField(src.config.source, c.field)) {
          add(
            'branch-field-unknown',
            `steps[${i}].config.field`,
            `"${c.field}" is not a field that source returns.`,
            s.id
          );
        }
        if (!hasText(c.otherwise))
          add(
            'branch-otherwise-required',
            `steps[${i}].config.otherwise`,
            'A branch on a field value needs an otherwise route.',
            s.id
          );
        return;
      }
      const outs = [...srcSpec.outcomes.positive, ...srcSpec.outcomes.negative];
      if (outs.length === 0) {
        add(
          'branch-source-no-outcomes',
          `steps[${i}].config.on`,
          `${srcSpec.label} records no outcome to branch on; branch on one of its fields instead.`,
          s.id
        );
        return;
      }
      c.routes.forEach((r, j) => {
        if (!outs.includes(r.when))
          add(
            'branch-outcome-unknown',
            `steps[${i}].config.routes[${j}].when`,
            `"${r.when}" is not an outcome of ${srcSpec.label} (${outs.join(', ')}).`,
            s.id
          );
      });
      const routed = new Set(c.routes.map((r) => r.when));
      if (!hasText(c.otherwise) && outs.some((o) => !routed.has(o))) {
        add(
          'branch-outcome-unrouted',
          `steps[${i}].config`,
          `Every outcome of ${srcSpec.label} needs a route (or an otherwise).`,
          s.id
        );
      }
    }
    if (Array.isArray(s.config && s.config.reviews)) {
      s.config.reviews.forEach((r, j) => {
        const target = byId.get(r);
        const tSpec = target && ACTION_BY_ID[target.action];
        if (
          !tSpec ||
          tSpec.strength === null ||
          tSpec.strength === 'None' ||
          !before.has(r) ||
          r === s.id
        ) {
          add(
            'reviews-input-invalid',
            `steps[${i}].config.reviews[${j}]`,
            'A reviewed input must be an evidence-producing step completed on every path to this one.',
            s.id
          );
        }
      });
    }
  });

  if (errors.length > 0) return done();

  // Paths: §3c rule 9, the Medium-or-higher rule, the expected strength.
  const { main, exhausted } = analysePaths(byId, scopes, indexOf, add);
  if (exhausted) return done();
  if (main.pass.length === 0) {
    add(
      'no-pass-end',
      'steps',
      'No path reaches an End with outcome pass, so no run of this definition could ever pass.'
    );
    return done();
  }
  main.pass.forEach((p) => {
    if (p.aware < MEDIUM) {
      add(
        'no-medium-evidence',
        `steps[${indexOf.get(p.endId)}]`,
        `A run can reach End "${p.endId}" (pass) without any Medium-or-higher evidence it can rest on. No workflow passes on Notify, Wait or Attest alone.`,
        p.endId
      );
    }
  });
  if (errors.length > 0) return done();

  analysis.expectedStrength = STRENGTHS[Math.min(...main.pass.map((p) => p.literal))];
  analysis.minimumPassStrength = STRENGTHS[Math.min(...main.pass.map((p) => p.aware))];
  return done();
}

/**
 * Counts per strength and the expected evidence strength of a definition
 * (design §3g): over every path to a pass End, the strongest evidence-bearing
 * action on it; the expected strength is the weakest of those. Derived by the
 * server at publish and frozen on the version — never client-supplied.
 *
 * The wording is "expected evidence strength", never a claim about a control.
 */
function expectedAssurance(def, options) {
  const counts = Object.fromEntries(STRENGTHS.map((s) => [s, 0]));
  const steps = isObj(def) && Array.isArray(def.steps) ? def.steps : [];
  steps.forEach((s) => {
    const spec = isObj(s) ? ACTION_BY_ID[s.action] : null;
    if (spec && spec.strength !== null) counts[spec.strength] += 1;
  });
  const { valid, analysis } = validateDefinition(def, options);
  const expectedStrength = valid ? analysis.expectedStrength : null;
  return {
    counts,
    expectedStrength,
    // The floor: the weakest evidence a passing run may rest on (a fallback
    // path). For display only — countability is always achieved >= expectedStrength.
    minimumPassStrength: valid ? analysis.minimumPassStrength : null,
    label: expectedStrength
      ? `Expected evidence strength: ${expectedStrength}`
      : 'Expected evidence strength: unknown — the definition does not validate',
  };
}

/**
 * The scopes of a definition the validator has accepted (design §3i "Scopes"):
 * every step's scope — 'main' or `${groupId}#${childIndex}` — and the scope
 * list with each child's group and dead-end flag. For the runner and the
 * assurance figure, so a run walks exactly the graph the validator judged.
 * Never call it on a definition that does not validate.
 *
 * @returns {{ scopeOf: Map<string, string>, scopes: Array<{ id, start, deadEnd, group }> }}
 */
function definitionScopes(def) {
  const steps = isObj(def) && Array.isArray(def.steps) ? def.steps.filter(isObj) : [];
  if (steps.length === 0) return { scopeOf: new Map(), scopes: [] };
  const byId = new Map(steps.map((s) => [s.id, s]));
  return assignScopes(byId, steps[0].id, () => {}, new Map());
}

module.exports = { validateDefinition, expectedAssurance, definitionScopes, MAX_PATHS, RULES_CHECKED };
